import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Alert, Row, Col, Card } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import Layout from "../../../layout/default";

const SupplierRegister = () => {
  const navigate = useNavigate();
  const [supplierData, setSupplierData] = useState({
    email: '',
    user_name: '',
    password: '',
    c_password: '',
    last_name: '',
    middle_name: '',
    phone_1: '',
    phone_2: '',
    aadhar_number: '',
    business_name: '',
    address: '',
    pincode: '',
    latitude: '', // Will be set using the Detect Location button
    longitude: '', // Will be set using the Detect Location button
    user_type: 2, // Default user type
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setSupplierData({ ...supplierData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const { email, password, c_password, phone_1, aadhar_number, pincode } = supplierData;
    const phoneRegex = /^[0-9]{10}$/;
    const aadharRegex = /^[0-9]{12}$/;
    const pincodeRegex = /^[0-9]{6}$/;

    // Validate email
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      Swal.fire('Error', 'Please enter a valid email address.', 'error');
      return false;
    }

    // Validate phone number
    if (!phone_1 || !phoneRegex.test(phone_1)) {
      Swal.fire('Error', 'Please enter a valid phone number (10 digits).', 'error');
      return false;
    }

    // Validate Aadhar number
    if (aadhar_number && !aadharRegex.test(aadhar_number)) {
      Swal.fire('Error', 'Please enter a valid Aadhar number (12 digits).', 'error');
      return false;
    }

    // Validate pincode
    if (!pincode || !pincodeRegex.test(pincode)) {
      Swal.fire('Error', 'Please enter a valid pincode (6 digits).', 'error');
      return false;
    }

    // Validate password and confirm password
    if (password !== c_password) {
      Swal.fire('Error', 'Passwords do not match.', 'error');
      return false;
    }

    // Validate password strength
    if (password && password.length < 6) {
      Swal.fire('Error', 'Password must be at least 6 characters long.', 'error');
      return false;
    }

    return true;
  };

  const detectLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setSupplierData({
            ...supplierData,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          Swal.fire({
            icon: 'success',
            title: 'Location Detected',
            text: `Latitude: ${position.coords.latitude}, Longitude: ${position.coords.longitude}`,
          });
        },
        (error) => {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Unable to fetch location. Please try again.',
          });
          console.error('Error detecting location:', error.message);
        }
      );
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Geolocation Not Supported',
        text: 'Your browser does not support geolocation.',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await axios.post(`${process.env.REACT_APP_HOST}api/register-vendor`, supplierData);

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'User registered successfully! Redirecting to login page...',
        timer: 1500,
        timerProgressBar: true,
        showConfirmButton: false,
      });

      console.log('Response:', response.data);

      // Navigate to the login page after a successful registration
      setTimeout(() => {
        navigate('/shop/auths/login');
      }, 1500);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Registration failed.';

      if (errorMessage === 'Email already exists.') {
        Swal.fire({
          icon: 'error',
          title: 'Registration Failed',
          text: 'The email address is already registered. Please use a different email.',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Registration Failed',
          text: errorMessage,
        });
      }
      console.error('Error:', err.response?.data);
    }
  };

  return (
    <Layout title="Supplier Registration">
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Row className="w-100 justify-content-center">
          <Col xs={12} md={6} lg={5}>
            <Card className="p-4 shadow-sm">
              <Card.Body>
                <h2 className="mb-4 text-center">Vendor Registration</h2>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={supplierData.email}
                      onChange={handleChange}
                      placeholder="Enter email"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="user_name"
                      value={supplierData.user_name}
                      onChange={handleChange}
                      placeholder="Enter user name"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Middle Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="middle_name"
                      value={supplierData.middle_name}
                      onChange={handleChange}
                      placeholder="Enter middle name"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="last_name"
                      value={supplierData.last_name}
                      onChange={handleChange}
                      placeholder="Enter last name"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Business Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="business_name"
                      value={supplierData.business_name}
                      onChange={handleChange}
                      placeholder="Enter business name"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={supplierData.password}
                      onChange={handleChange}
                      placeholder="Enter password"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="c_password"
                      value={supplierData.c_password}
                      onChange={handleChange}
                      placeholder="Confirm password"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone 1</Form.Label>
                    <Form.Control
                      type="text"
                      name="phone_1"
                      value={supplierData.phone_1}
                      onChange={handleChange}
                      placeholder="Enter primary phone number"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone 2 (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      name="phone_2"
                      value={supplierData.phone_2}
                      onChange={handleChange}
                      placeholder="Enter secondary phone number"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Aadhar Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="aadhar_number"
                      value={supplierData.aadhar_number}
                      onChange={handleChange}
                      placeholder="Enter Aadhar number"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      type="text"
                      name="address"
                      value={supplierData.address}
                      onChange={handleChange}
                      placeholder="Enter address"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Pincode</Form.Label>
                    <Form.Control
                      type="text"
                      name="pincode"
                      value={supplierData.pincode}
                      onChange={handleChange}
                      placeholder="Enter pincode"
                      required
                    />
                  </Form.Group>
                  <Button variant="secondary" onClick={detectLocation} className="w-100 mb-3">
                    Detect Location
                  </Button>
                  <Button variant="primary" type="submit" className="register-button w-100">
                    Register
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <style jsx>{`
      @media (max-width: 768px) {
  .register-button {
    margin-bottom: 50px; /* Adjust as needed */
    position: relative;
    z-index: 1;
  }

  .footer {
    position: relative;
    z-index: 0;
  }
}
  `}</style>
    </Layout>
  );
};

export default SupplierRegister;
